import * as React from "react"
import { Box, Flex } from "@chakra-ui/react"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import MediaPlayer from "../../_mediaPlayer"
import { BigPlayButton } from "../BigPlayButton"
import { ButtonsMainScreen } from "../../buttons-main-screen"
import { mainWindowUserSelector } from "../../../../recoil/atoms/mainWindowUser"
import { SDKContext } from "../../../../context/SDKContext"
import { LocalUserProperties } from "../../../../types/localUserProps"
import { useBoolean } from "../../../../hooks/useBoolean"
import { usePictureInPicture } from "../../../../hooks/usePictureInPicture"
import { brandingSelector, showTabsAtBottomOnMobileSelector } from "../../../../recoil/atoms/room"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import { useWatchParty } from "../../../../hooks/useWatchParty"
import useWindowSize from "../../../../hooks/useWindowSize"
import { findSideRatio } from "../../../../util/findSideRatio"
import { SimpleGrid } from "@chakra-ui/react"
import ScreenOverlay from "../../_screenOverlay"
import { usersSelector } from "../../../../recoil/atoms/users"
import { pipParamsSelector } from "../../../../recoil/atoms/pictureInPicture"
import { playerPropsSelector } from "../../../../recoil/atoms/player"
import { currentVisibleOverlayAtom, overlaySelector, showBadgeOverlayAtom } from "../../../../recoil/atoms/overlay"
import { productOverlaySelector } from "../../../../recoil/atoms/product-overlay"
import { MobileHeader } from "../mobile-header"
import { ProductOverlay } from "../../../servicesPanel/badges/product-selector/overlay-product"
import { MediaController } from "../../videoPlayer/media-controller"
import layoutStates from "../../../../recoil/atoms/layout"
import { FullScreenIcon } from "../../buttons-main-screen/full-screen-icon"
import { FloatingControlsCtx } from "../../floatingControls"
import { withErrorBoundary } from "react-error-boundary"
import ComponentFallback from "src/components/common/errors/componentFallback"

interface MobileWebCameraMainScreenProps {
  localUserProperties: LocalUserProperties
  isLandscape: boolean
  videoSize: {
    width: number
    height: number
  }
  isOnlyScreensView: boolean
  hideUserAvatar: boolean
  positionOverlay: any
  showLeaveOverlay: boolean
  setShowLeaveOverlay: any
  isTransparent: boolean
  productSelectorSizeOverlayProps: any
  isUrlBarNarrowed: boolean
  changeSizeIconDependingHeightVideo?: (
    minHeightIcon: number,
    maxHeightIcon: number
  ) => number
}

export const MobileWebCameraMainScreen: React.FC<MobileWebCameraMainScreenProps> = ({
  localUserProperties,
  isLandscape,
  videoSize,
  isOnlyScreensView,
  hideUserAvatar,
  setShowLeaveOverlay,
  showLeaveOverlay,
  positionOverlay,
  isTransparent,
  productSelectorSizeOverlayProps,
  isUrlBarNarrowed,
  changeSizeIconDependingHeightVideo
}) => {
  const overlay = useRecoilValue(overlaySelector)
  const [{ isOpen }, setProductOverlay] = useRecoilState(productOverlaySelector)
  const mainWindowUser = useRecoilValue(mainWindowUserSelector)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const timeout = React.useRef(null)
  const { screenSize } = useWindowSize()
  const roomStyles = useRecoilValue(brandingSelector)
  const meetingClient = React.useContext(SDKContext)
  const [wasCameraEnabled] = useBoolean(false)
  const { isSafariMobile, isSafari } = useBrowserType()
  const { isWatchParty, watchPartyType, watchPartyHostId } = useWatchParty()
  const { isActivePiP } = useRecoilValue(pipParamsSelector)
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
  const data = React.useContext(FloatingControlsCtx)
  // const isShareLinksModal = useRecoilValue(shareLinksModalAtom)
  const showAvatar = roomStyles.enableDigitalAvatar
  const enableLeaveOverlay = !isWatchParty || showAvatar

  const { leavePictureinPicture, initStream, exitPictureInPicture, videoPiP } =
    usePictureInPicture()

  const container = document.getElementById(
    `player-1337-${mainWindowUser.uid}`
  ) as HTMLDivElement
  const video = container?.childNodes[0]?.childNodes[0] as HTMLVideoElement

  const mainWindowUserDetails = useRecoilValue(
    usersSelector(mainWindowUser?.uid)
  )

  const currentMediaStreamTrack: MediaStreamTrack =
    meetingClient.getMediaTracks(mainWindowUser?.uid)?.videoTrack
      ?._mediaStreamTrack

  React.useEffect(() => {
    if (!mainWindowUserDetails?.camEnabled && !isWatchParty) {
      exitPictureInPicture()
    }
    timeout.current = setTimeout(() => {
      initStream(currentMediaStreamTrack)
    }, 500)
    return () => clearTimeout(timeout.current)
  }, [
    initStream,
    currentMediaStreamTrack,
    exitPictureInPicture,
    mainWindowUserDetails?.camEnabled,
    isWatchParty
  ])

  const handleEnterMobilePictureInPicture = React.useCallback(() => {
    /* if (localUserProperties.camEnabled) {
        meetingClient.toggleCam(true)
        setWasCameraEnabled.on()
      } */
    videoPiP.play()
  }, [videoPiP])

  const handleLeaveMobilePictureInPicture = React.useCallback(() => {
    setProductOverlay({ isOpen: false })
    if (wasCameraEnabled) {
      /* meetingClient.toggleCam(false) */
      /* setWasCameraEnabled.off() */
    }

    leavePictureinPicture()
    videoPiP.play()
  }, [setProductOverlay, wasCameraEnabled, leavePictureinPicture, videoPiP])

  React.useEffect(() => {
    if (videoPiP) {
      videoPiP.addEventListener(
        "leavepictureinpicture",
        handleLeaveMobilePictureInPicture
      )
      videoPiP.addEventListener(
        "enterpictureinpicture",
        handleEnterMobilePictureInPicture
      )
      return () => {
        videoPiP.removeEventListener(
          "leavepictureinpicture",
          handleLeaveMobilePictureInPicture
        )
        videoPiP.removeEventListener(
          "enterpictureinpicture",
          handleEnterMobilePictureInPicture
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPiP])

  const setPiPParams = useSetRecoilState(pipParamsSelector)

  const handleEnterMobilePiPAfterFS = () => {
    setPiPParams({
      isActivePiP: true,
      isRedirected: true,
      coverImage: overlay ? overlay.coverImage : ""
    })
  }

  React.useEffect(() => {
    if (video) {
      video.addEventListener(
        "enterpictureinpicture",
        handleEnterMobilePiPAfterFS
      )
      video.addEventListener(
        "leavepictureinpicture",
        handleLeaveMobilePictureInPicture
      )
      return () => {
        video.removeEventListener(
          "enterpictureinpicture",
          handleEnterMobilePiPAfterFS
        )
        videoPiP.removeEventListener(
          "leavepictureinpicture",
          handleLeaveMobilePictureInPicture
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video])

  const defaultHeightMainScreen = React.useMemo<number>(() => {
    return findSideRatio(screenSize.width, 9, 16)
  }, [screenSize.width])

  const defaultWidthMainScreen = React.useMemo<number>(() => {
    return findSideRatio(screenSize.height, 16, 9)
  }, [screenSize.height])

  const mainScreenStyling: React.CSSProperties = React.useMemo(() => {
    return {
      backgroundColor:
        isLandscape && isWatchParty && isSafari
          ? "unset !!!important"
          : roomStyles?.alertBackgroundColor ?? "black",
      width: isFloatingDivActive
        ? "100%"
        : isLandscape
        ? defaultWidthMainScreen + "px"
        : videoSize.width + "px",
      height: isFloatingDivActive
        ? "100%"
        : isOnlyScreensView
        ? `${defaultHeightMainScreen}px`
        : isLandscape
        ? screenSize.height + "px"
        : videoSize.height + "px",
      ObjectFit: "cover",
      maxWidth: screenSize.width + "px",
      zIndex: 11,
      padding: isWatchParty && isLandscape && isSafari && "0px 30px"
    }
  }, [
    isLandscape,
    isWatchParty,
    isSafari,
    isFloatingDivActive,
    roomStyles?.alertBackgroundColor,
    defaultWidthMainScreen,
    videoSize.width,
    videoSize.height,
    isOnlyScreensView,
    defaultHeightMainScreen,
    screenSize.height,
    screenSize.width
  ])

  const heightLeaveOverlay = React.useMemo<string>(
    () => "calc(100% - 50px)",
    []
  )

  const isMobilePiPEnabled = React.useMemo(
    () => isActivePiP && document.pictureInPictureEnabled,
    // && mainWindowUserDetails?.camEnabled,
    [isActivePiP]
  )

  const handleShowLeaveOverlay = React.useCallback(
    e => {
      if (!isOpen) {
        if (showLeaveOverlay) {
          setPlayerProps({
            isOpenControlsMobile: false,
            isOpenControlDesktop: false
          })
        }
        if (!showLeaveOverlay) {
          setPlayerProps({
            isOpenControlsMobile: true,
            isOpenControlDesktop: false
          })
        }
        setShowLeaveOverlay(!showLeaveOverlay)
      }
      e.stopPropagation()
    },
    [isOpen, showLeaveOverlay, setShowLeaveOverlay, setPlayerProps]
  )

  const _overlay_ = useRecoilValue(currentVisibleOverlayAtom)
  const [isOverlayBadge] = useRecoilState<boolean>(showBadgeOverlayAtom)

  const isOverlay = React.useMemo(
    () => _overlay_ && isOverlayBadge,
    [_overlay_, isOverlayBadge]
  )
  const showTabsAtBottomOnMobile = useRecoilValue(
    showTabsAtBottomOnMobileSelector
  )

  const isPartiallyScrolled = (() =>
    !isOverlay &&
    ((isTransparent && !showTabsAtBottomOnMobile) || isLandscape))()

  return (
    <>
      <Box className="mainScreenOuter" overflow="hidden">
        <MobileHeader isLandscape={isLandscape} isVideoHeader={true} />

        {!isFloatingDivActive &&
          // if cam is active
          mainWindowUserDetails?.camEnabled &&
          // and if only audio watch party is active
          (!isWatchParty || watchPartyType === "audio") && (
            <Flex
              position="absolute"
              top="10px"
              right={"6px"}
              zIndex="10000000000 !important"
            >
              <FullScreenIcon
                isFullScreenMobile={true}
                handleShowsFullScreen={() => {}}
                isFullScreen={false}
                isTablet={false}
                isMobile={true}
              />
            </Flex>
          )}

        {/* {isShareLinksModal && <ShareLinksModal isTablet={true} isMobile={true}/>} */}
        <MediaPlayer
          uid={mainWindowUser.uid}
          localUserProperties={
            localUserProperties.id === mainWindowUser.uid
              ? localUserProperties
              : {}
          }
          styling={mainScreenStyling}
          index={1337}
          setShowLeaveOverlay={setShowLeaveOverlay}
          showLeaveOverlay={enableLeaveOverlay && showLeaveOverlay}
          showLeave={enableLeaveOverlay}
          showNBAOverlay={false}
          isLandscape={isLandscape}
          isSmallWindow={false}
          overlay={
            <>
              {!isWatchParty && !isFloatingDivActive && (
                <SimpleGrid
                  id="fade-badge"
                  columns={{ base: 1, md: 2 }}
                  zIndex="popover"
                  position="absolute"
                  paddingLeft="0px !important"
                  display="flex"
                  left="0px"
                  flexDirection="column"
                  bottom={positionOverlay}
                  onTouchEnd={handleShowLeaveOverlay}
                  /* onClick={handleShowLeaveOverlay} */
                >
                  <ScreenOverlay
                    isMobile={true}
                    width={
                      isLandscape
                        ? findSideRatio(screenSize.height, 16, 9) * 0.5
                        : videoSize.width - 60
                    }
                    maxWidthOverlay={
                      isLandscape
                        ? findSideRatio(screenSize.height, 16, 9) * 0.5
                        : videoSize.width - 60
                    }
                    maxWidthChatOverlay={
                      isLandscape
                        ? findSideRatio(screenSize.height, 16, 9) * 0.5
                        : videoSize.width - 60
                    }
                    isTouch={true}
                    isSmallMainScreen={true}
                    productSelectorSizeOverlayProps={
                      productSelectorSizeOverlayProps
                    }
                  />
                </SimpleGrid>
              )}
            </>
          }
          isSafariMobile={isSafariMobile}
          hideAvatar={
            hideUserAvatar || (isWatchParty && watchPartyType !== "audio")
          }
          isMainMobileScreen={true}
          isMobilePiPEnabled={isMobilePiPEnabled}
          heightLeaveOverlay={heightLeaveOverlay}
        />

        {!isFloatingDivActive && (
          <div
            style={{
              position: "absolute",
              bottom: "0px",
              left: "0px",
              zIndex: 100000000
            }}
          >
            <ProductOverlay
              width={isLandscape ? defaultWidthMainScreen : videoSize.width}
              height={isLandscape ? screenSize.height : videoSize.height}
            />
          </div>
        )}

        {((!isWatchParty && !isFloatingDivActive) ||
          roomStyles.enableDigitalAvatar) && (
          <MediaController
            {...{
              showLeave: true,
              isMobile: true,
              isLandscape,
              showLeaveOverlay,
              setShowLeaveOverlay,
              localUserProperties,
              mainWindowUserId: mainWindowUser?.uid
            }}
          >
            {!isOpen && (
              <Flex
                pos="absolute"
                id="container-button-main-screen"
                // bottom={isLandscape ? "5px" : isTransparent ? "10px" : "0px"}
                // bottom={"57px"}
                bottom={isPartiallyScrolled ? "124px" : "58px"}
                // bottom="88px"
                right={isLandscape ? "4.5px" : "0px"}
                padding={
                  isLandscape
                    ? "10px 0px 10px 0px"
                    : isTransparent
                    ? "10px 6px 0px 0px"
                    : "10px 5px 0px 10px"
                }
                zIndex="10001 !important"
                flexDirection="column"
                justifyContent="space-between"
              >
                <ButtonsMainScreen
                  isLandscape={isLandscape}
                  localUserProperties={localUserProperties}
                  isTransparent={isTransparent}
                  isMobile={true}
                  changeSizeIconDependingHeightVideo={
                    changeSizeIconDependingHeightVideo
                  }
                  showLeaveButton={
                    !data?.isPlayerFocused && (data?.isMobile || data?.isTablet)
                  }
                />
              </Flex>
            )}
          </MediaController>
        )}
      </Box>
    </>
  )
}
